import React from 'react';
import { UserIcon, BotIcon } from './Icons';
import MarkdownRenderer from './MarkdownRenderer';
import MessageFeedback from './MessageFeedback';
import { formatMessageTime } from '../utils/date';

import type { MessageSource } from '../services/gateway/types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string | Date;
  source?: MessageSource;
}

interface ChatMessageProps {
  message: Message;
  onFeedback?: (messageId: string, feedback: 'like' | 'dislike' | null) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onFeedback }) => {
  const isUser = message.role === 'user';
  const isFallback = message.source === 'fallback' && !isUser;

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isUser ? 'row-reverse' : 'row',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 16px',
    marginBottom: '4px',
  };

  const avatarStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isUser ? '#33AA40' : '#394E5B',
    flexShrink: 0,
  };

  const bubbleStyle: React.CSSProperties = {
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    backgroundColor: isUser ? '#33AA40' : '#ffffff',
    color: isUser ? '#ffffff' : '#262626',
    border: isUser ? 'none' : isFallback ? '1px solid rgba(245,166,35,0.4)' : '1px solid rgba(0,0,0,0.1)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const fallbackBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10px',
    color: '#F5A623',
    marginTop: '2px',
    fontWeight: 500,
  };

  const fallbackDotStyle: React.CSSProperties = {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: '#F5A623',
  };

  const timestampStyle: React.CSSProperties = {
    fontSize: '11px',
    color: isUser ? 'rgba(255,255,255,0.7)' : '#999999',
    marginTop: '4px',
    textAlign: isUser ? 'right' : 'left',
  };

  return (
    <div style={rowStyle}>
      <div style={avatarStyle}>
        {isUser ? (
          <UserIcon size={16} color="#ffffff" />
        ) : (
          <BotIcon size={16} color="#ffffff" />
        )}
      </div>
      <div style={{ maxWidth: '75%' }}>
        <div style={bubbleStyle}>
          <MarkdownRenderer content={message.content} darkBackground={isUser} />
        </div>
        <div style={timestampStyle}>
          {formatMessageTime(message.timestamp)}
        </div>
        {isFallback && (
          <div style={fallbackBadgeStyle}>
            <div style={fallbackDotStyle} />
            <span>Fallback</span>
          </div>
        )}
        {!isUser && (
          <MessageFeedback messageId={message.id} onFeedback={onFeedback} />
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
